import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Send, Loader2, CheckCircle2, FileDown } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogTrigger,
} from "@/components/ui/dialog";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const inputClass =
  "w-full bg-white/5 border border-white/10 px-4 py-3 text-white placeholder:text-gray-500 font-light focus:outline-none focus:ring-2 focus:ring-gold focus:border-gold transition-all duration-200 rounded-none";

const SampleReportModal = ({ triggerClassName, triggerLabel = "Request a sample report" }) => {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "" });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const reset = () => {
    setForm({ name: "", email: "", phone: "" });
    setDone(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) {
      toast.error("Please enter your name and email.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, {
        ...form,
        service: "Sample Report Request",
        message: "Requested a sample HHSRS hazard report via the website.",
      });
      setDone(true);
      toast.success("Sample sent — check your inbox (and spam). You can also download it now.");
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        setOpen(v);
        if (!v) setTimeout(reset, 200);
      }}
    >
      <DialogTrigger asChild>
        <button
          data-testid="sample-report-trigger"
          className={
            triggerClassName ||
            "inline-flex items-center gap-2 border border-white/20 hover:border-gold text-white hover:text-gold font-semibold uppercase tracking-[0.12em] px-8 py-4 transition-colors duration-300"
          }
        >
          <FileDown className="h-4 w-4" /> {triggerLabel}
        </button>
      </DialogTrigger>

      <DialogContent className="bg-surface border border-white/10 rounded-none text-white sm:max-w-md">
        {done ? (
          <div data-testid="sample-report-success" className="text-center py-6">
            <CheckCircle2 className="h-14 w-14 text-gold mx-auto mb-5" strokeWidth={1.5} />
            <DialogTitle className="font-heading text-2xl font-bold mb-2">Request received.</DialogTitle>
            <DialogDescription className="text-gray-400 font-light">
              Thank you. We've emailed a sample HHSRS hazard report to {form.email || "you"} — you can also download it right now below.
            </DialogDescription>
            <a
              href="/downloads/OLV-HHSRS-Sample-Report.pdf"
              target="_blank"
              rel="noopener noreferrer"
              download
              data-testid="sample-report-download"
              className="mt-6 inline-flex items-center justify-center gap-2 bg-gold hover:bg-gold-hover text-midnight font-semibold uppercase tracking-[0.12em] px-8 py-3.5 transition-colors duration-300"
            >
              <FileDown className="h-4 w-4" /> Download the sample now
            </a>
            <div>
              <button
                data-testid="sample-report-close"
                onClick={() => setOpen(false)}
                className="mt-5 text-gold hover:text-gold-hover uppercase tracking-[0.12em] text-sm font-semibold"
              >
                Close
              </button>
            </div>
          </div>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="font-heading text-2xl font-bold tracking-tight">
                Request a sample report
              </DialogTitle>
              <DialogDescription className="text-gray-400 font-light">
                See the standard of our HHSRS hazard reporting. Enter your details
                and we'll send a sample straight to your inbox.
              </DialogDescription>
            </DialogHeader>
            <form data-testid="sample-report-form" onSubmit={handleSubmit} className="space-y-4 mt-2">
              <div>
                <label htmlFor="sample-name" className="block text-sm text-gray-300 font-light mb-2">Full name *</label>
                <input id="sample-name" data-testid="sample-input-name" name="name" value={form.name} onChange={handleChange}
                  placeholder="Jane Smith" className={inputClass} required />
              </div>
              <div>
                <label htmlFor="sample-email" className="block text-sm text-gray-300 font-light mb-2">Email *</label>
                <input id="sample-email" data-testid="sample-input-email" type="email" name="email" value={form.email} onChange={handleChange}
                  placeholder="jane@email.com" className={inputClass} required />
              </div>
              <div>
                <label htmlFor="sample-phone" className="block text-sm text-gray-300 font-light mb-2">Phone</label>
                <input id="sample-phone" data-testid="sample-input-phone" name="phone" value={form.phone} onChange={handleChange}
                  placeholder="07..." className={inputClass} />
              </div>
              <button data-testid="sample-submit-button" type="submit" disabled={submitting}
                className="group w-full inline-flex items-center justify-center gap-2 bg-gold hover:bg-gold-hover disabled:opacity-60 text-midnight font-semibold uppercase tracking-[0.12em] px-8 py-3.5 transition-colors duration-300">
                {submitting ? (
                  <><Loader2 className="h-4 w-4 animate-spin" /> Sending...</>
                ) : (
                  <>Send me a sample <Send className="h-4 w-4 group-hover:translate-x-1 transition-transform" /></>
                )}
              </button>
            </form>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default SampleReportModal;
